import { useMemo, useState } from 'react';
import {
  Button,
  FeatureFlags,
  Search,
  Tag,
  TreeNode,
  TreeView,
} from '@carbon/react';
import { Checkmark, ListChecked } from '@carbon/react/icons';
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from 'components/accordion/accordion';
import { colorForArea } from 'components/type-curve-workspace/area-colors';
import {
  treeFormationName,
  type InventoryTreePackage,
  type InventoryTypeCurveTree as InventoryTypeCurveTreeData,
  type InventoryTypeCurveTreeArea,
} from 'types/api-responses';
import {
  resolveTreeLeaves,
  treeInventoryPackages,
  treePackageCount,
  treePackageLabel,
  treePackagesInUse,
  type InventoryTreeLeafRef,
} from './tree-leaves';
import { formationSwatchColor } from './formation-swatch-colors';
import style from './inventory-workspace.module.scss';

interface InventoryTypeCurveTreeProps {
  tree: InventoryTypeCurveTreeData;
  /** The selected leaf ref's stable key. */
  selectedKey: string | null;
  onSelect: (leaf: InventoryTreeLeafRef) => void;
  /**
   * Opens the add/remove picker, where curves are both added and dropped —
   * this tree stays a read-only view of the result. Omitted on a promoted
   * (read-only) workspace, which hides the control entirely rather than
   * disabling it.
   */
  onEditCurves?: () => void;
  /**
   * A package node was clicked: select `leaf` (the formation's type curve) on
   * that package. Omitted, package nodes are listed but not selectable.
   */
  onSelectPackage?: (
    leaf: InventoryTreeLeafRef,
    inventoryFormationPackageId: number
  ) => void;
  /**
   * The server's per-formation default packages. Passed separately because a
   * picked package is moved to the front of `tree` as the formation's
   * selection, which is not the same thing as being its default.
   */
  defaultPackageIds?: ReadonlySet<number>;
}

/**
 * A package node's id.
 * @param {number} inventoryFormationPackageId - The package.
 * @return {string} The node id.
 */
const packageNodeId = (inventoryFormationPackageId: number) =>
  `pkg-${inventoryFormationPackageId}`;
/**
 * An area node's id.
 * @param {InventoryTypeCurveTreeArea} area - The area.
 * @return {string} The node id.
 */
const areaNodeId = (area: InventoryTypeCurveTreeArea) =>
  `area-${area.typeCurveArea}`;
/**
 * A formation node's id.
 * @param {InventoryTypeCurveTreeArea} area - The formation's area.
 * @param {string} formationName - The formation.
 * @return {string} The node id.
 */
const formationNodeId = (
  area: InventoryTypeCurveTreeArea,
  formationName: string
) => `area-${area.typeCurveArea}-formation-${formationName}`;

interface PackageNodeLabelProps {
  pkg: InventoryTreePackage;
  /** The formation is on this package: its slots and gun barrel band read it. */
  inUse: boolean;
  /** The server's default package for the formation. */
  isDefault: boolean;
}

/**
 * A package node's label: the name, a check on the package its formation is
 * on, and the Default tag. No leading icon: the indent alone marks it as a
 * child of the type curve.
 * @param {PackageNodeLabelProps} props - The package and its flags.
 * @return {JSX.Element} The label.
 */
function PackageNodeLabel({ pkg, inUse, isDefault }: PackageNodeLabelProps) {
  return (
    <span className={style.leaf}>
      <span className={style.packageName}>
        <span className={style.itemName}>{treePackageLabel(pkg)}</span>
        {inUse && (
          <Checkmark
            size={16}
            className={style.packageInUse}
            aria-label="In use"
          />
        )}
      </span>
      {isDefault && (
        <span className={style.itemTags}>
          <Tag type="cool-gray" size="sm">
            Default
          </Tag>
        </span>
      )}
    </span>
  );
}

interface FormationChildrenArgs {
  leaves: InventoryTreeLeafRef[];
  packages: InventoryTreePackage[];
  selectedKey: string | null;
  packagesInUse: ReadonlySet<number>;
  defaultPackageIds?: ReadonlySet<number>;
  onSelect: (leaf: InventoryTreeLeafRef) => void;
  onSelectPackage?: InventoryTypeCurveTreeProps['onSelectPackage'];
  isExpanded: (nodeId: string) => boolean;
  onToggle: (nodeId: string) => void;
}

/**
 * A formation's child nodes: its type curve, with the formation's inventory
 * packages (zones) one level below it. The checked package is the one the
 * formation is on, which its slots, gun barrel band and slot table rows read;
 * Default is the server's default. The tree shows one curve per formation, so
 * the packages hang off the selected curve (else the first); a formation with
 * no curve lists them directly. A plain render function rather than a
 * component: TreeView clones its TreeNode children with depth and selection
 * props, which a wrapper component would swallow.
 * @param {FormationChildrenArgs} args - The formation's leaves and packages.
 * @return {JSX.Element[]} The child nodes.
 */
function formationChildren({
  leaves,
  packages,
  selectedKey,
  packagesInUse,
  defaultPackageIds,
  onSelect,
  onSelectPackage,
  isExpanded,
  onToggle,
}: FormationChildrenArgs) {
  // A package click keeps the formation's type curve: the selected one when it
  // is in this formation.
  const packageLeaf =
    leaves.find((leaf) => leaf.key === selectedKey) ?? leaves[0];

  const packageNodes = packages.map((pkg) => {
    const pkgId = pkg.inventoryFormationPackageId;
    return (
      <TreeNode
        key={packageNodeId(pkgId)}
        id={packageNodeId(pkgId)}
        label={
          <PackageNodeLabel
            pkg={pkg}
            inUse={packagesInUse.has(pkgId)}
            isDefault={
              defaultPackageIds ? defaultPackageIds.has(pkgId) : pkg.isDefault
            }
          />
        }
        onSelect={() => {
          if (packageLeaf && onSelectPackage) {
            onSelectPackage(packageLeaf, pkgId);
          }
        }}
      />
    );
  });

  if (!packageLeaf) return packageNodes;

  return leaves.map((leaf) => {
    const nodeId = `tc-${leaf.key}`;
    const holdsPackages = leaf === packageLeaf && packageNodes.length > 0;
    return (
      <TreeNode
        key={leaf.key}
        id={nodeId}
        label={
          <span className={style.leaf}>
            <span className={style.itemName}>{leaf.label}</span>
            {/* The tree shows one curve per formation — the formation's
                default/selected curve — so it always carries the Default chip. */}
            <span className={style.itemTags}>
              <Tag type="cool-gray" size="sm">
                Default
              </Tag>
            </span>
          </span>
        }
        onSelect={() => onSelect(leaf)}
        {...(holdsPackages
          ? {
              isExpanded: isExpanded(nodeId),
              onToggle: () => onToggle(nodeId),
            }
          : {})}
      >
        {holdsPackages ? packageNodes : undefined}
      </TreeNode>
    );
  });
}

/**
 * Client-side filter: an area/formation match keeps its whole subtree, a leaf
 * or package match keeps its ancestors.
 * @param {InventoryTypeCurveTreeArea[]} areas - The tree's areas.
 * @param {string} query - The raw search input.
 * @return {InventoryTypeCurveTreeArea[]} The areas that survive the filter.
 */
function filterAreas(
  areas: InventoryTypeCurveTreeArea[],
  query: string
): InventoryTypeCurveTreeArea[] {
  const q = query.trim().toLowerCase();
  if (!q) return areas;

  return areas.flatMap((area) => {
    if (area.typeCurveArea.toLowerCase().includes(q)) return [area];

    const formations = area.formations.flatMap((formation) => {
      if (treeFormationName(formation).toLowerCase().includes(q)) {
        return [formation];
      }
      const typeCurves = formation.typeCurves.filter(
        (leaf) =>
          leaf.label.toLowerCase().includes(q) ||
          leaf.uniqueTcId.toLowerCase().includes(q)
      );
      const inventoryPackages = treeInventoryPackages(formation).filter((pkg) =>
        treePackageLabel(pkg).toLowerCase().includes(q)
      );
      return typeCurves.length > 0 || inventoryPackages.length > 0
        ? [{ ...formation, typeCurves, inventoryPackages }]
        : [];
    });

    return formations.length > 0 ? [{ ...area, formations }] : [];
  });
}

/**
 * The workspace's left-nav: areas -> formations -> type curves -> inventory
 * packages, from the show payload's typeCurveTree, matching the Inventory
 * Workspace mock (v14): Carbon TreeView (xs) with area colour rules, formation
 * strat swatches, Origin/Default tags and a client-side search. Selecting a
 * leaf drives the page's inventory-details and header fetches; selecting a
 * package puts its formation on that package.
 * @param {InventoryTypeCurveTreeProps} props - Tree data, selection and callback.
 * @return {JSX.Element} The searchable tree.
 */
export const InventoryTypeCurveTree = ({
  tree,
  selectedKey,
  onSelect,
  onEditCurves,
  onSelectPackage,
  defaultPackageIds,
}: InventoryTypeCurveTreeProps) => {
  const [query, setQuery] = useState('');
  // Collapsed node ids — everything starts expanded (trees are small). While a
  // search is active, matches render force-expanded and the set is ignored.
  const [collapsed, setCollapsed] = useState<ReadonlySet<string>>(new Set());
  const searching = query.trim().length > 0;

  const visibleAreas = useMemo(
    () => filterAreas(tree.areas, query),
    [tree.areas, query]
  );

  // Palette slots follow the map's area layer (`colorForArea`): the origin area
  // owns slot 0 (red), neighbours take the following slots in tree order.
  // Computed from the FULL tree so colours hold steady while searching.
  const areaColorIndexes = useMemo(() => {
    const indexes = new Map<string, number>();
    let next = 1;
    for (const area of tree.areas) {
      indexes.set(area.typeCurveArea, area.isOrigin ? 0 : next++);
    }
    return indexes;
  }, [tree.areas]);

  const leafRefs = useMemo(() => resolveTreeLeaves(tree), [tree]);

  const leafRefsByZone = useMemo(() => {
    const grouped = new Map<string, InventoryTreeLeafRef[]>();
    for (const leaf of leafRefs) {
      const zoneKey = `${leaf.typeCurveArea}|${leaf.formation}`;
      const existing = grouped.get(zoneKey);
      if (existing) {
        existing.push(leaf);
      } else {
        grouped.set(zoneKey, [leaf]);
      }
    }
    return grouped;
  }, [leafRefs]);

  // From the FULL tree, so the in-use marker holds steady while searching.
  const packagesInUse = useMemo(() => treePackagesInUse(tree), [tree]);

  const toggleNode = (nodeId: string) => {
    setCollapsed((prev) => {
      const next = new Set(prev);
      if (next.has(nodeId)) {
        next.delete(nodeId);
      } else {
        next.add(nodeId);
      }
      return next;
    });
  };

  const isExpanded = (nodeId: string) => searching || !collapsed.has(nodeId);
  const selectedNodeId = selectedKey !== null ? `tc-${selectedKey}` : null;
  // The selected leaf's package is highlighted with it: the pair is the
  // selection the gun barrel and slot table read.
  const selectedPackageId =
    leafRefs.find((leaf) => leaf.key === selectedKey)
      ?.inventoryFormationPackageId ?? null;
  const selectedNodeIds = [
    selectedNodeId,
    selectedPackageId !== null ? packageNodeId(selectedPackageId) : null,
  ].filter((nodeId): nodeId is string => nodeId !== null);

  return (
    <Accordion defaultExpanded className={style.treeAccordion}>
      <AccordionSummary>
        <div className={style.typeCurveTreeTitle}>
          {`Type Curves (${tree.typeCurveCount}) · Inventories (${treePackageCount(
            tree
          )})`}
        </div>
      </AccordionSummary>
      <AccordionDetails>
        <div className={style.typeCurveTree}>
          {onEditCurves && (
            <div className={style.actionsRow}>
              <Button
                kind="ghost"
                size="sm"
                renderIcon={ListChecked}
                onClick={onEditCurves}
              >
                Add or remove
              </Button>
            </div>
          )}
          <Search
            size="sm"
            labelText="Search type curves and inventories"
            placeholder="Search curves, inventories, area, formation..."
            value={query}
            onChange={(event) => setQuery(event.target.value)}
          />
          {tree.areas.length === 0 ? (
            <p className={style.treeEmpty}>No type curves in this workspace.</p>
          ) : visibleAreas.length === 0 ? (
            <p className={style.treeEmpty}>No matching type curves.</p>
          ) : (
            <div className={style.treeScroll}>
              <FeatureFlags enableTreeviewControllable>
                <TreeView
                  label="Type Curves"
                  hideLabel
                  size="xs"
                  active={selectedNodeId ?? undefined}
                  selected={selectedNodeIds}
                >
                  {visibleAreas.map((area) => (
                    <TreeNode
                      key={areaNodeId(area)}
                      id={areaNodeId(area)}
                      label={
                        <span className={style.nodeLabel}>
                          <span
                            className={style.areaRule}
                            style={{
                              background: colorForArea(
                                areaColorIndexes.get(area.typeCurveArea) ?? 0
                              ),
                            }}
                          />
                          <span className={style.groupLabel}>
                            {area.typeCurveArea}
                          </span>
                          {area.isOrigin && (
                            <Tag type="teal" size="sm">
                              Origin
                            </Tag>
                          )}
                          <span className={style.groupCount}>
                            ({area.typeCurveCount})
                          </span>
                        </span>
                      }
                      isExpanded={isExpanded(areaNodeId(area))}
                      onToggle={() => toggleNode(areaNodeId(area))}
                    >
                      {area.formations.map((formation) => {
                        const nodeId = formationNodeId(
                          area,
                          treeFormationName(formation)
                        );
                        const leaves =
                          leafRefsByZone.get(
                            `${area.typeCurveArea}|${treeFormationName(
                              formation
                            )}`
                          ) ?? [];
                        return (
                          <TreeNode
                            key={nodeId}
                            id={nodeId}
                            label={
                              <span className={style.nodeLabel}>
                                <span
                                  className={style.stratSwatch}
                                  style={{
                                    background: formationSwatchColor(formation),
                                  }}
                                />
                                <span className={style.groupLabel}>
                                  {treeFormationName(formation)}
                                </span>
                                <span className={style.groupCount}>
                                  ({formation.typeCurves.length})
                                </span>
                              </span>
                            }
                            isExpanded={isExpanded(nodeId)}
                            onToggle={() => toggleNode(nodeId)}
                          >
                            {formationChildren({
                              leaves,
                              packages: treeInventoryPackages(formation),
                              selectedKey,
                              packagesInUse,
                              defaultPackageIds,
                              onSelect,
                              onSelectPackage,
                              isExpanded,
                              onToggle: toggleNode,
                            })}
                          </TreeNode>
                        );
                      })}
                    </TreeNode>
                  ))}
                </TreeView>
              </FeatureFlags>
            </div>
          )}
        </div>
      </AccordionDetails>
    </Accordion>
  );
};
